import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { api, smpwPath } from "./api";
import QueryKeys from "./queryKeys";
import {
  AddRemoveLocationAssignmentMasks,
  AddRemoveUserAvailabilities,
  AddRemoveZoneAssignments,
  hgdbSMPWUserPermission,
  hgdbSMPWUserZoneAssignment,
  hgdbSMPWZone,
  hgdbSMPWZoneDefault,
  hgdbSMPWZoneSetting,
  PresignedPost,
  SMPWAddRemovePairings,
  SMPWAddRemoveWaitlist,
  SMPWAutoFillRequestStatus,
  SMPWEmailRequest,
  SMPWInvite,
  SMPWLocation,
  SMPWMy,
  SMPWNewCongInput,
  SMPWPairing,
  SMPWRegisterNewZone,
  SMPWReport,
  SMPWSchedule,
  SMPWUser,
  SMPWUserAvailability,
  SMPWUserLocationAssignmentMask,
  SMPWZone,
  SMPWZoneDefaults,
  SMPWZoneSetting,
  SMPWZoneWaitlist,
  ZoneGeo,
} from "../types/smpw";
import { nameOfUser } from "../helpers/user";
import ky from "ky";
import { Congregation } from "../types/scheduling/weekend";
import { RegistrationModel } from "../types/register/registerModel";
import dayjs from "../helpers/dayjs";
import { ExportSMPW, SMPWNotification } from "../types/export";
import { ActivityEvent } from "../types/activity";

export class SMPWAPI {
  base = smpwPath("");

  private smpwScheduleExtendedOptions = {
    parseJson: (data: string) =>
      JSON.parse(data, (key, value) => (["datetime", "submitted_on", "date"].includes(key) ? dayjs.utc(value) : value)),
  };

  async regZone(model: SMPWRegisterNewZone): Promise<void> {
    const url = `${this.base}/regzone`;
    return api.post(url, { json: model }).json();
  }

  async regUser(model: RegistrationModel): Promise<ZoneGeo> {
    const url = `${this.base}/reg/user`;
    return api.post(url, { json: model }).json();
  }

  async getGeoInfo(lat: number, lng: number): Promise<ZoneGeo> {
    const url = `${this.base}/reg`;
    return api.put(url, { json: { lat: lat, lng: lng } }).json();
  }

  async getZoneDefaults(): Promise<SMPWZoneDefaults[]> {
    const url = `${this.base}/zones/defaults`;
    return api.get(url).json();
  }

  async setZoneDefault(input: hgdbSMPWZoneDefault): Promise<hgdbSMPWZoneDefault> {
    const url = `${this.base}/zones/defaults`;
    return api.put(url, { json: input }).json();
  }

  async deleteZoneDefault(input: hgdbSMPWZoneDefault): Promise<void> {
    const url = `${this.base}/zones/defaults`;
    return api.delete(url, { json: input }).json();
  }

  async getZones(): Promise<SMPWZone> {
    const url = `${this.base}/zones`;
    return api.get(url).json();
  }

  async setZone(input: hgdbSMPWZone): Promise<SMPWZone> {
    const url = `${this.base}/zones`;
    return api.put(url, { json: input }).json();
  }

  async deleteZone(input: hgdbSMPWZone): Promise<hgdbSMPWZone> {
    const url = `${this.base}/zones`;
    return api.delete(url, { json: input }).json();
  }

  static userFromResponse(user: SMPWUser): SMPWUser {
    user.displayName = nameOfUser(user);
    if (!user.per_day) user.per_day = 1;
    if (!user.per_week) user.per_week = 1;
    if (!user.per_month) user.per_month = 1;
    return user;
  }

  async getUsers(): Promise<SMPWUser[]> {
    const url = `${this.base}/users`;
    const data: SMPWUser[] = await api.get(url).json();
    return await Promise.all(data.map((u: SMPWUser) => SMPWAPI.userFromResponse(u)));
  }

  async setUser(user: SMPWUser): Promise<SMPWUser> {
    const url = `${this.base}/users`;
    return api.put(url, { json: user }).json();
  }

  async deleteUser(user: SMPWUser): Promise<SMPWUser> {
    const url = `${this.base}/users`;
    return api.delete(url, { json: user }).json();
  }

  async seachUserEmail(email: string): Promise<SMPWUser> {
    const url = `${this.base}/search/users`;
    const u: SMPWUser = await api.post(url, { json: { email: email } }).json();

    return SMPWAPI.userFromResponse(u);
  }

  async getPermissions(): Promise<hgdbSMPWUserPermission[]> {
    const url = `${this.base}/permissions`;
    return api.get(url).json();
  }

  async setPermissions(input: hgdbSMPWUserPermission[]): Promise<hgdbSMPWUserPermission[]> {
    const url = `${this.base}/permissions`;
    return api.put(url, { json: input }).json();
  }

  async getLocations(): Promise<SMPWLocation[]> {
    const url = `${this.base}/locations`;
    return api.get(url).json();
  }

  async setLocation(input: SMPWLocation): Promise<SMPWLocation> {
    const url = `${this.base}/locations`;
    return api.put(url, { json: input }).json();
  }

  async deleteLocation(input: SMPWLocation): Promise<SMPWLocation> {
    const url = `${this.base}/locations`;
    return api.delete(url, { json: input }).json();
  }

  async getAssignments(): Promise<hgdbSMPWUserZoneAssignment[]> {
    const url = `${this.base}/assignments`;
    return api.get(url).json();
  }

  async setAssignments(input: AddRemoveZoneAssignments): Promise<hgdbSMPWUserZoneAssignment[]> {
    const url = `${this.base}/assignments`;
    return api.put(url, { json: input }).json();
  }

  async getWaitList(): Promise<SMPWZoneWaitlist[]> {
    const url = `${this.base}/waitlist`;
    return api.extend(this.smpwScheduleExtendedOptions).get(url).json();
  }

  async setWaitList(input: SMPWAddRemoveWaitlist): Promise<SMPWZoneWaitlist[]> {
    const url = `${this.base}/waitlist`;
    return api.put(url, { json: input }).json();
  }

  async getLocationMasks(): Promise<SMPWUserLocationAssignmentMask[]> {
    const url = `${this.base}/location-masks`;
    return api.get(url).json();
  }

  async setLocationMasks(input: AddRemoveLocationAssignmentMasks): Promise<SMPWUserLocationAssignmentMask[]> {
    const url = `${this.base}/location-masks`;
    return api.put(url, { json: input }).json();
  }

  async getUserSchedules(userID: number): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { userID: userID } })
      .json();
  }

  async getLocationSchedules(locID: number, from: string, to: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/loc`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { locID: locID, from: from, to: to } })
      .json();
  }

  async getZoneSchedules(zoneID: number, from: string, to: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/zone`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { zoneID: zoneID, from: from, to: to } })
      .json();
  }

  async generateSchedules(zone_id: number, month: string): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/generate`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .post(url, { json: { zone_id: zone_id, month: month } })
      .json();
  }

  async autofillSchedules(zone_id: number, month: string): Promise<void> {
    const url = `${this.base}/schedules/autofill`;
    await api.post(url, { json: { zone_id: zone_id, month: month } });
  }

  async getAutoFillRequestStatus(zone_id: number, month: string): Promise<SMPWAutoFillRequestStatus> {
    const url = `${this.base}/schedules/autofill/status`;
    return api.get(url, { searchParams: { zone_id: zone_id, month: month } }).json();
  }

  async deleteSchedules(zone_id: number, from: string, to: string, clear_only: boolean): Promise<SMPWSchedule[]> {
    const url = `${this.base}/schedules/range`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .delete(url, { json: { id: zone_id, from: from, to: to, clear_only: clear_only } })
      .json();
  }

  async deleteShift(schedules: SMPWSchedule[]): Promise<void> {
    const url = `${this.base}/schedules`;
    return api.extend(this.smpwScheduleExtendedOptions).delete(url, { json: schedules }).json();
  }

  async setSchedules(input: SMPWSchedule[]): Promise<void> {
    const url = `${this.base}/schedules`;
    return api.put(url, { json: input }).json();
  }

  async getAvailabilities(): Promise<SMPWUserAvailability[]> {
    const url = `${this.base}/availabilities`;
    return api.get(url).json();
  }

  async setAvailabilities(input: AddRemoveUserAvailabilities): Promise<void> {
    const url = `${this.base}/availabilities`;
    return api.put(url, { json: input }).json();
  }

  async getZoneSettings(zone_id: number): Promise<SMPWZoneSetting> {
    const url = `${this.base}/settings/${zone_id}`;
    return api.get(url).json();
  }

  async getAllZonesSettings(): Promise<SMPWZoneSetting[]> {
    const url = `${this.base}/settings`;
    return api.get(url).json();
  }

  async setSettings(input: hgdbSMPWZoneSetting): Promise<void> {
    const url = `${this.base}/settings`;
    return api.put(url, { json: input }).json();
  }

  async getPairings(): Promise<SMPWPairing[]> {
    const url = `${this.base}/pairings`;
    return api.get(url).json();
  }

  async setPairings(input: SMPWAddRemovePairings): Promise<void> {
    const url = `${this.base}/pairings`;
    return api.put(url, { json: input }).json();
  }

  async getMy(): Promise<SMPWMy> {
    const url = `${this.base}/my`;
    const my: SMPWMy = await api.extend(this.smpwScheduleExtendedOptions).get(url).json();
    my.self = SMPWAPI.userFromResponse(my.self);
    return my;
  }

  async getNearbyCongregations(): Promise<Congregation[]> {
    const url = `${this.base}/congs`;
    return api.get(url).json();
  }

  async addCongregations(input: SMPWNewCongInput, forReg = false): Promise<Congregation> {
    const url = forReg ? `${this.base}/reg/congs` : `${this.base}/congs`;
    return api.put(url, { json: input }).json();
  }

  // emailLoginInvite has the publisher emailed an invitation to set up their login. It's only ever emailed, so nothing
  // is returned
  async emailLoginInvite(userId: number): Promise<void> {
    const url = `fsreport/invite/${userId}`;
    await api.put(url);
  }

  // getSMPWInvites get all invitations for an SMPW Zones
  async getSMPWInvites(): Promise<SMPWInvite[]> {
    const url = `${this.base}/invite/zone`;
    return api.get(url).json();
  }

  // createSMPWInvite creates an invitation for a user to an SMPW Zones
  async createSMPWInvite(user: SMPWUser): Promise<SMPWInvite> {
    const url = `${this.base}/invite`;
    return api.put(url, { json: user }).json();
  }

  // createSMPWInvite creates an invitation for a user to an SMPW Zones
  async inviteAllSMPW(): Promise<void> {
    const url = `${this.base}/invite/all`;
    return api.post(url).json();
  }

  async deleteSMPWInvite(invite: SMPWInvite): Promise<void> {
    const url = `${this.base}/invite`;
    return api.delete(url, { json: invite }).json();
  }

  async respondToMyInvite(invite: SMPWInvite): Promise<void> {
    const url = `${this.base}/invite`;
    return api.post(url, { json: invite }).json();
  }

  async sendBulkEmail(input: SMPWEmailRequest): Promise<void> {
    const url = `${this.base}/email`;
    return api.post(url, { json: input }).json();
  }

  async getReports(from: string, to: string): Promise<SMPWReport[]> {
    const url = `${this.base}/reports`;
    return api
      .extend(this.smpwScheduleExtendedOptions)
      .get(url, { searchParams: { from: from, to: to } })
      .json();
  }

  async setReport(input: SMPWReport): Promise<SMPWReport> {
    const url = `${this.base}/reports`;
    return api.put(url, { json: input }).json();
  }

  async deleteReport(input: SMPWReport): Promise<SMPWReport> {
    const url = `${this.base}/reports`;
    return api.delete(url, { json: input }).json();
  }

  async exportSMPW(): Promise<ExportSMPW> {
    const url = `${this.base}/export`;
    return api.get(url).json();
  }

  async importLocust(file: File): Promise<void> {
    const url = `${this.base}/import-locust`;
    return api.post(url, { body: file, headers: { "Content-Type": "application/octet-stream" } }).json();
  }

  async getSMPWNotification(from: string, to: string, zoneID: number): Promise<SMPWNotification[]> {
    const url = `${this.base}/notifications/${from}_${to}/${zoneID}`;
    return api.get(url).json();
  }

  async getActivityLogs(from: string, to: string): Promise<ActivityEvent[]> {
    const url = `${this.base}/activity/${from}_${to}`;
    return api.get(url).json();
  }

  async sendSMPWNotifications(from: string, to: string, zoneID: number): Promise<void> {
    const url = `${this.base}/notifications/${from}_${to}/${zoneID}`;
    return api.put(url).json();
  }

  private async getPresignedURL(id: number, type: "user" | "location"): Promise<PresignedPost> {
    const url = `${this.base}/images/presigned`;
    let payload;
    switch (type) {
      case "user":
        payload = { user_id: id };
        break;
      case "location":
        payload = { location_id: id };
    }

    return api.post(url, { json: payload }).json();
  }

  async uploadImage(id: number, file: File, type: "user" | "location"): Promise<string | undefined> {
    const psp = await this.getPresignedURL(id, type);
    const url = psp.URL;

    const formData = new FormData();
    Object.entries(psp.Values).forEach(([k, v]) => {
      formData.append(k, v);
    });
    formData.append("file", file);

    const key = psp.Values["key"];
    const extApi = ky.create();
    await extApi.post(url, { body: formData }).json();
    return key;
  }
}

export function useSMPWWaitList(options?: UseQueryOptions<SMPWZoneWaitlist[], Error>) {
  return useQuery<SMPWZoneWaitlist[], Error>({
    queryKey: [QueryKeys.SMPWZoneWaitlist],
    queryFn: () => smpwAPI.getWaitList(),
    ...options,
  });
}

export function useSMPWLocationMasks(options?: UseQueryOptions<SMPWUserLocationAssignmentMask[], Error>) {
  return useQuery<SMPWUserLocationAssignmentMask[], Error>({
    queryKey: [QueryKeys.SMPWUserLocationAssignmentMask],
    queryFn: () => smpwAPI.getLocationMasks(),
    ...options,
  });
}

export function useSMPWZoneInvites(options?: UseQueryOptions<SMPWInvite[], Error>) {
  return useQuery<SMPWInvite[], Error>({
    queryKey: [QueryKeys.SMPWZoneInvites],
    queryFn: () => smpwAPI.getSMPWInvites(),
    ...options,
  });
}

export function useSMPWMy(options?: UseQueryOptions<SMPWMy, Error>) {
  return useQuery<SMPWMy, Error>({
    queryKey: [QueryKeys.SMPWMy],
    queryFn: () => smpwAPI.getMy(),
    ...options,
  });
}

export function useSMPWZones(options?: UseQueryOptions<SMPWZone, Error>) {
  return useQuery<SMPWZone, Error>({
    queryKey: [QueryKeys.SMPWZone],
    queryFn: () => smpwAPI.getZones(),
    ...options,
  });
}

export function useSMPWZoneDefaults(options?: UseQueryOptions<SMPWZoneDefaults[], Error>) {
  return useQuery<SMPWZoneDefaults[], Error>({
    queryKey: [QueryKeys.SMPWZoneDefaults],
    queryFn: () => smpwAPI.getZoneDefaults(),
    ...options,
  });
}

export function useSMPWUsers(options?: UseQueryOptions<SMPWUser[], Error>) {
  return useQuery<SMPWUser[], Error>({
    queryKey: [QueryKeys.SMPWUsers],
    queryFn: () => smpwAPI.getUsers(),
    ...options,
  });
}

export function useSMPWAssignments(options?: UseQueryOptions<hgdbSMPWUserZoneAssignment[], Error>) {
  return useQuery<hgdbSMPWUserZoneAssignment[], Error>({
    queryKey: [QueryKeys.SMPWUserZoneAssignment],
    queryFn: () => smpwAPI.getAssignments(),
    ...options,
  });
}

export function useSMPWPermissions(options?: UseQueryOptions<hgdbSMPWUserPermission[], Error>) {
  return useQuery<hgdbSMPWUserPermission[], Error>({
    queryKey: [QueryKeys.SMPWUserPermissions],
    queryFn: () => smpwAPI.getPermissions(),
    ...options,
  });
}

export function useSMPWLocations(options?: UseQueryOptions<SMPWLocation[], Error>) {
  return useQuery<SMPWLocation[], Error>({
    queryKey: [QueryKeys.SMPWLocations],
    queryFn: () => smpwAPI.getLocations(),
    ...options,
  });
}

export function useSMPWReports(from: string, to: string, options?: UseQueryOptions<SMPWReport[], Error>) {
  return useQuery<SMPWReport[], Error>({
    queryKey: [QueryKeys.SMPWReports, from, to],
    queryFn: () => smpwAPI.getReports(from, to),
    ...options,
  });
}

export function useSMPWActivity(from: string, to: string, options?: Partial<UseQueryOptions<ActivityEvent[], Error>>) {
  return useQuery<ActivityEvent[], Error>({
    queryKey: [QueryKeys.SMPWActivity, from, to],
    queryFn: () => smpwAPI.getActivityLogs(from, to),
    ...options,
  });
}

export function useSMPWAvailabilities(options?: UseQueryOptions<SMPWUserAvailability[], Error>) {
  return useQuery<SMPWUserAvailability[], Error>({
    queryKey: [QueryKeys.SMPWUserAvailabilities],
    queryFn: () => smpwAPI.getAvailabilities(),
    ...options,
  });
}

export function useSMPWPairings(options?: UseQueryOptions<SMPWPairing[], Error>) {
  return useQuery<SMPWPairing[], Error>({
    queryKey: [QueryKeys.SMPWPairings],
    queryFn: () => smpwAPI.getPairings(),
    ...options,
  });
}

export function useSMPWZoneSettings(options?: UseQueryOptions<SMPWZoneSetting[], Error>) {
  return useQuery<SMPWZoneSetting[], Error>({
    queryKey: [QueryKeys.SMPWZoneSetting],
    queryFn: () => smpwAPI.getAllZonesSettings(),
    ...options,
  });
}

export function useSMPWUserSchedules(user_id: number, options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWUserSchedules, user_id],
    queryFn: () => smpwAPI.getUserSchedules(user_id),
    ...options,
  });
}

export function useSMPWNotifications(
  from: string,
  to: string,
  zoneID: number,
  options?: UseQueryOptions<SMPWNotification[], Error>,
) {
  return useQuery<SMPWNotification[], Error>({
    queryKey: [QueryKeys.SMPWNotifications, from, to, zoneID],
    queryFn: () => smpwAPI.getSMPWNotification(from, to, zoneID),
    ...options,
  });
}

export function useSMPWLocationSchedules(
  loc_id: number,
  from: string,
  to: string,
  options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>,
) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWLocSchedules, loc_id, from, to],
    queryFn: () => smpwAPI.getLocationSchedules(loc_id, from, to),
    ...options,
  });
}

export function useSMPWZoneSchedules(
  zone_id: number,
  from: string,
  to: string,
  options?: Partial<UseQueryOptions<SMPWSchedule[], Error>>,
) {
  return useQuery<SMPWSchedule[], Error>({
    queryKey: [QueryKeys.SMPWZoneSchedules, zone_id, from, to],
    queryFn: () => smpwAPI.getZoneSchedules(zone_id, from, to),
    ...options,
  });
}

export function useNearbyCongregations(options?: Partial<UseQueryOptions<Congregation[], Error>>) {
  return useQuery<Congregation[], Error>({
    queryKey: [QueryKeys.SMPWNearbyCongregations],
    queryFn: () => smpwAPI.getNearbyCongregations(),
    ...options,
  });
}

export function useAutoFillRequestStatus(
  zone_id: number,
  month: string,
  options?: Partial<UseQueryOptions<SMPWAutoFillRequestStatus, Error>>,
) {
  return useQuery<SMPWAutoFillRequestStatus, Error>({
    queryKey: [QueryKeys.AutoFillRequestStatus, zone_id, month],
    queryFn: () => smpwAPI.getAutoFillRequestStatus(zone_id, month),
    ...options,
  });
}

export const smpwAPI = new SMPWAPI();
